import React, { useState, useCallback } from 'react';
import type { Driver, Vehicle, Id } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useVehicleUpdateApi } from '@hero/api-hooks';
import { toast } from 'sonner';
import { ModalForm } from '../../../app/Modal';
import { VehicleSelector, type VehicleSelectionValue } from '../../Vehicle/Selector/VehicleSelector';

export interface DriverAssignVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Driver;
}

export function DriverAssignVehicleModal({
  isOpen,
  onClose,
  driver,
}: DriverAssignVehicleModalProps) {
  const { t } = useTranslation(['m-driver', 'c-main']);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | undefined>(undefined);

  const currentVehicleId = driver.vehicle?._id;
  const effectiveVehicleId: Id | undefined = selectedVehicle?._id ?? currentVehicleId;
  const selectorValue: VehicleSelectionValue = selectedVehicle ?? currentVehicleId;

  const { mutate: updateVehicle, isPending, isSuccess } = useVehicleUpdateApi({
    onSuccess: () => {
      toast.success(t('toast.success.vehicle_assigned'));
      onClose();
      setSelectedVehicle(undefined);
    },
    onError: (error: Error) => {
      toast.error(t('toast.error.vehicle_assign', { message: error.message }));
    },
  });

  const handleSave = useCallback(() => {
    if (!effectiveVehicleId) return;
    updateVehicle({
      _id: effectiveVehicleId,
      driverId: String(driver._id),
    } as any);
  }, [effectiveVehicleId, driver._id, updateVehicle]);

  const handleClose = () => {
    setSelectedVehicle(undefined);
    onClose();
  };

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSave}
      title={t('modal.assign_vehicle_title')}
      description={t('modal.assign_vehicle_description', { name: driver.name })}
      isLoading={isPending}
      isSuccess={isSuccess}
      size="md"
      saveText={t('modal.assign')}
      disableSave={!effectiveVehicleId}>
      <VehicleSelector
        selectedVehicle={selectorValue}
        onVehicleSelected={vehicles => setSelectedVehicle(vehicles?.[0])}
        label={t('modal.select_vehicle_placeholder')}
      />
    </ModalForm>
  );
}
